import { AdditionalInsight, ConsumerMarketConfidence } from '../interfaces/interface.consumer-market-confidence';
import { state as autoCheckState } from '../store/store.autocheck';
import { state as consumerMarketConfidenceState } from '../store/store.consumer-market-confidence';
import { state as offerState } from '../store/store.offer';
import { state as vehicleState } from '../store/store.offer-vehicle';
import { state as optimizelyState } from '../store/store.optimizely';
import { state as pageParamsState } from '../store/store.page-params';
import { DEPRECIATION_DISCLAIMER, SCARCITY_RANGES, TAX_SAVINGS, TAX_SAVINGS_DISCLAIMER, THIRTY_DAY_DEPRECIATION } from '../utils/utils.consumer-market-confident';
import { isEmptyValue } from '../utils/utils.js';
import { formatPriceWithSign } from '../utils/utils.offer';

import { isNeedsReviewOffer } from './actions.offer';

const keyFactorsCount = (): number => {
  const { keyFactors = [] } = consumerMarketConfidenceState;
  return keyFactors.filter((factor: ConsumerMarketConfidence) => factor.shouldDisplay).length;
};

const isKBB = (): boolean => {
  const { valuation = [] } = offerState || {};
  if (!valuation || valuation.length === 0) {
    return false;
  }
  const { initialPriceType } = valuation[0] || {};

  return initialPriceType === 'KBB';
};

export const onConsumerMarketConfidenceInit = (): void => {
  const offerReview = isNeedsReviewOffer();
  const { isConsumerMarketConfidenceOn } = optimizelyState;
  const { isAdmin } = pageParamsState;
  const { autoCheckFullReportLink } = autoCheckState;
  const { valuation, showDealerListForPrint, isKelleyBuysYourCar, potentialTaxSaving, isInspectionNeeded, showCustomerDealerList, isOfficeClosed, isFurtherReviewNeeded } =
    offerState;
  const { uvmmrColorAdjustment, uvmmrConditionAdjustment, mileageAdjustment, uvmmrOptionsAdjustment } = valuation[0] || {};

  const keyFactors: ConsumerMarketConfidence[] = [
    {
      type: 'Mileage',
      shouldDisplay: !isKBB() && mileageAdjustment !== null,
      value: mileageAdjustment,
      description: getMileageDescription(),
      isPositiveValue: mileageAdjustment >= 0,
      displayValue: formatPriceWithSign(mileageAdjustment),
      titlePrice: 'Mileage',
    },
    {
      type: 'Color',
      shouldDisplay: !isKBB() && !isEmptyValue(uvmmrColorAdjustment),
      value: uvmmrColorAdjustment,
      description: getColorDescription(),
      isPositiveValue: uvmmrColorAdjustment >= 0,
      displayValue: formatPriceWithSign(uvmmrColorAdjustment),
      titlePrice: 'Color',
    },
    {
      type: 'Equipment',
      shouldDisplay: !isKBB() && !isEmptyValue(getEquipmentDescription()) && uvmmrOptionsAdjustment !== null,
      value: uvmmrOptionsAdjustment,
      description: getEquipmentDescription(),
      isPositiveValue: uvmmrOptionsAdjustment >= 0,
      displayValue: formatPriceWithSign(uvmmrOptionsAdjustment),
      titlePrice: 'Equipment',
    },
    {
      type: 'Condition',
      shouldDisplay: !isKBB() && !isEmptyValue(getConditionDescription()) && uvmmrConditionAdjustment !== null,
      value: uvmmrConditionAdjustment,
      description: getConditionDescription(),
      isPositiveValue: uvmmrConditionAdjustment >= 0,
      displayValue: formatPriceWithSign(uvmmrConditionAdjustment),
      hasIcon: true,
      titlePrice: 'Condition',
    },
  ];

  const thirtyDayDepreciation = getThirtyDayDepreciation();
  const additionalInsights: AdditionalInsight[] = [
    {
      type: TAX_SAVINGS,
      shouldDisplay: shouldShowTaxSavings(),
      value: potentialTaxSaving,
      description: 'Receive sales tax savings when you sell and purchase at the same dealership',
      isPositiveValue: potentialTaxSaving >= 0,
      displayValue: formatPriceWithSign(potentialTaxSaving),
      titlePrice: 'ESTIMATED SAVINGS',
      disclaimers: shouldShowTaxSavings() ? TAX_SAVINGS_DISCLAIMER : null,
    },
    {
      type: THIRTY_DAY_DEPRECIATION,
      shouldDisplay: shouldShowThirtyDayDepreciation(),
      value: thirtyDayDepreciation,
      description: 'Based on similar vehicles (year, make, model and style)',
      isPositiveValue: thirtyDayDepreciation >= 0,
      displayValue: formatPriceWithSign(thirtyDayDepreciation),
      titlePrice: '30 DAY PREDICTION',
      disclaimers: shouldShowThirtyDayDepreciation() ? DEPRECIATION_DISCLAIMER : null,
    },
  ];

  const shouldDisplay = isConsumerMarketConfidenceOn && !isAdmin;
  consumerMarketConfidenceState.keyFactors = keyFactors;
  consumerMarketConfidenceState.shouldDisplayKeyFactors = shouldDisplay && keyFactorsCount() >= 2;

  if (offerReview) {
    consumerMarketConfidenceState.shouldDisplayDealerListInfo = !isKelleyBuysYourCar && isInspectionNeeded && showCustomerDealerList && !isAdmin;
    consumerMarketConfidenceState.shouldDisplayExpectDealership = !isKelleyBuysYourCar && isInspectionNeeded && showCustomerDealerList && !isAdmin;
    consumerMarketConfidenceState.shouldDisplayPrepareForVisit = !isAdmin && isInspectionNeeded && showCustomerDealerList;
    consumerMarketConfidenceState.shouldDisplayConfidentVehicleHistoryReport =
      !(isOfficeClosed || isFurtherReviewNeeded) && !isInspectionNeeded && autoCheckFullReportLink.length > 1 && !isKelleyBuysYourCar && !isAdmin;
  } else {
    consumerMarketConfidenceState.shouldDisplayExpectDealership = !isAdmin;
    consumerMarketConfidenceState.shouldDisplayDealerListInfo = showDealerListForPrint && !isAdmin;
    consumerMarketConfidenceState.shouldDisplayPrepareForVisit = !isAdmin;
  }

  consumerMarketConfidenceState.shouldDisplayAdditionalInsights = shouldDisplay && (shouldShowThirtyDayDepreciation() || shouldShowTaxSavings());
  consumerMarketConfidenceState.additionalInsights = additionalInsights;
};

export const getMileageDescription = (): string => {
  const { valuation = [] } = offerState;
  const { mileageAdjustment, kbbMileageAdjustment } = valuation[0] || {};
  const mileageValue = isKBB() ? kbbMileageAdjustment : mileageAdjustment;

  let mileageComparison = '';

  if (mileageValue > 0) {
    mileageComparison = 'lower than';
  } else if (mileageValue < 0) {
    mileageComparison = 'higher than';
  } else {
    mileageComparison = 'about the same as';
  }

  return `Your vehicle mileage is ${mileageComparison} similar vehicles in the market.`;
};

export const getColorDescription = (): string => {
  const { valuation = [] } = offerState;
  const { uvmmrColorAdjustment } = valuation[0] || {};
  const { color } = vehicleState;
  let colorComparison = '';

  if (uvmmrColorAdjustment > 0) {
    colorComparison = 'is a desirable color for this vehicle.';
  } else if (uvmmrColorAdjustment < 0) {
    colorComparison = 'is less desirable for this vehicle.';
  }

  return colorComparison ? `${color} ${colorComparison}` : '';
};

export const getEquipmentDescription = (): string => {
  const { valuation = [] } = offerState;
  const { uvmmrOptionsAdjustment, uvmmrEquipmentIndex } = valuation[0] || {};
  let equipmentComparison = '';

  if (isEmptyValue(uvmmrEquipmentIndex)) {
    return '';
  }
  if (uvmmrEquipmentIndex >= SCARCITY_RANGES.HIGHLY_DESIRABLE.MIN && uvmmrEquipmentIndex <= SCARCITY_RANGES.HIGHLY_DESIRABLE.MAX && uvmmrOptionsAdjustment > 0) {
    equipmentComparison = 'more equipped than';
  } else if (uvmmrEquipmentIndex >= SCARCITY_RANGES.AVERAGE.MIN && uvmmrEquipmentIndex <= SCARCITY_RANGES.AVERAGE.MAX) {
    equipmentComparison = 'equally equipped to';
  } else if (uvmmrEquipmentIndex >= SCARCITY_RANGES.LESS_DESIRABLE.MIN && uvmmrEquipmentIndex <= SCARCITY_RANGES.LESS_DESIRABLE.MAX && uvmmrOptionsAdjustment < 0) {
    equipmentComparison = 'less equipped than';
  }

  return equipmentComparison ? `Your vehicle is ${equipmentComparison} similar vehicles in the market.` : '';
};

export const getColorAndEquipmentDescription = (): string => {
  const { kbbOptionAdjustment } = offerState;

  if (kbbOptionAdjustment > 0) {
    return 'Your vehicle is more equipped than similar vehicles in the market.';
  } else if (kbbOptionAdjustment < 0) {
    return 'Your vehicle is less equipped than similar vehicles in the market.';
  }

  return 'Your vehicle is equally equipped to similar vehicles in the market.';
};

export const getConditionDescription = (): string => {
  const { valuation = [] } = offerState;
  const { uvmmrGrade } = valuation[0] || {};

  if (uvmmrGrade === undefined || uvmmrGrade === null) {
    return '';
  }
  if (uvmmrGrade >= 4) {
    return 'You told us your car has little or no damage.';
  }
  if (uvmmrGrade > 3) {
    return 'Your car has some damage.';
  }
  return 'Your car has a fair amount of damage, so your Offer reflects that.';
};

const getThirtyDayDepreciation = (): number => {
  const { currentWeeklyDepreciation } = offerState;
  if (isEmptyValue(currentWeeklyDepreciation)) {
    return null;
  }
  // The 30-day depreciation is calculated by taking the VINsights 'current_weekly_depreciation' field and multiplying it by 4.
  const depreciation = currentWeeklyDepreciation * 4;
  return parseFloat(depreciation.toFixed(0));
};

export const shouldShowThirtyDayDepreciation = (): boolean => {
  return getThirtyDayDepreciation() < 0;
};

export const shouldShowTaxSavings = (): boolean => {
  const { potentialTaxSaving } = offerState;
  return potentialTaxSaving > 0;
};

const actions = {
  onConsumerMarketConfidenceInit,
  getMileageDescription,
  getColorDescription,
  getEquipmentDescription,
  getConditionDescription,
  shouldShowThirtyDayDepreciation,
  shouldShowTaxSavings,
};

export { actions };
export default actions;
